import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X, Video, FileText } from 'lucide-react';
import FileUploader from '@/components/ui/file-uploader';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Course {
  id?: string;
  title: string;
  description: string;
  category: string;
  video_url?: string;
  workbook_url?: string;
  thumbnail_url?: string;
  duration_minutes?: number;
  difficulty_level: string;
  tags: string[];
  is_published: boolean;
}

export default function CoursesManager() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultCourse: Course = {
    title: '',
    description: '',
    category: 'general',
    difficulty_level: 'beginner',
    tags: [],
    is_published: false
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const saveCourse = async (course: Course) => {
    try {
      if (course.id) {
        const { error } = await supabase
          .from('courses')
          .update(course)
          .eq('id', course.id);
        if (error) throw error;
        toast.success('Course updated successfully');
      } else {
        const { error } = await supabase
          .from('courses')
          .insert([{ ...course, user_id: (await supabase.auth.getUser()).data.user?.id }]);
        if (error) throw error;
        toast.success('Course created successfully');
      }
      
      loadCourses();
      setEditingCourse(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error('Failed to save course');
    }
  };

  const deleteCourse = async (id: string) => {
    try {
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Course deleted successfully');
      loadCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const CourseForm = ({ course, onSave, onCancel }: { 
    course: Course; 
    onSave: (course: Course) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(course);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{course.id ? 'Edit Course' : 'Create New Course'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Course title"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Course description"
              rows={4}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="healing">Healing</SelectItem>
                  <SelectItem value="spirituality">Spirituality</SelectItem>
                  <SelectItem value="relationships">Relationships</SelectItem>
                  <SelectItem value="manifestation">Manifestation</SelectItem>
                  <SelectItem value="trauma">Trauma Recovery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="difficulty">Difficulty Level</Label>
              <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <FileUploader
              bucketName="course-videos"
              allowedTypes={['mp4', 'mov', 'avi', 'mkv']}
              maxSizeMB={100}
              currentUrl={formData.video_url}
              onUploadComplete={(url) => setFormData({ ...formData, video_url: url })}
              label="Course Video"
              placeholder="Or paste video URL (YouTube, Vimeo, etc.)"
            />
          </div>
          
          <div>
            <FileUploader
              bucketName="course-workbooks"
              allowedTypes={['pdf', 'doc', 'docx']}
              maxSizeMB={50}
              currentUrl={formData.workbook_url}
              onUploadComplete={(url) => setFormData({ ...formData, workbook_url: url })}
              label="Workbook/Materials"
              placeholder="Or paste workbook URL"
            />
          </div>
          
          <div>
            <FileUploader
              bucketName="course-thumbnails"
              allowedTypes={['jpg', 'jpeg', 'png', 'webp']}
              maxSizeMB={10}
              currentUrl={formData.thumbnail_url}
              onUploadComplete={(url) => setFormData({ ...formData, thumbnail_url: url })}
              label="Thumbnail Image"
              placeholder="Or paste image URL"
            />
          </div>
          
          <div>
            <Label htmlFor="duration">Duration (minutes)</Label>
            <Input
              id="duration"
              type="number"
              value={formData.duration_minutes || ''}
              onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || undefined })}
              placeholder="60"
            />
          </div>
          
          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
              })}
              placeholder="healing, spirituality, growth"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
            />
            <Label htmlFor="is_published">Published</Label>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading courses...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Courses Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Course
        </Button>
      </div>
      
      {isCreating && (
        <CourseForm
          course={defaultCourse}
          onSave={saveCourse}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingCourse && (
        <CourseForm
          course={editingCourse}
          onSave={saveCourse}
          onCancel={() => setEditingCourse(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((course) => (
          <Card key={course.id} className={!course.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{course.title}</CardTitle>
                  <Badge variant="secondary">{course.category}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCourse(course)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => course.id && deleteCourse(course.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{course.description}</p>
              <div className="flex items-center gap-2 mb-2">
                {course.video_url && <Video className="h-4 w-4 text-primary" />}
                {course.workbook_url && <FileText className="h-4 w-4 text-primary" />}
                {course.duration_minutes && (
                  <span className="text-xs text-muted-foreground">{course.duration_minutes}m</span>
                )}
              </div>
              {course.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {course.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                {course.difficulty_level} | {course.is_published ? 'Published' : 'Draft'}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}